import { useState } from 'react';
import { Button, useToast } from '@caruso/ui';
import { Copy, CheckCircle, QrCode as QrCodeIcon } from 'lucide-react';
import QRCode from 'react-qr-code';

interface PixPaymentProps {
  pixData: {
    qrCode: string;
    copyPaste: string;
    expirationDate: string;
  };
}

export function PixPayment({ pixData }: PixPaymentProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(pixData.copyPaste);
      setCopied(true);
      toast({
        title: 'Código PIX copiado!',
        description: 'Cole no seu app de pagamentos para efetuar o pagamento',
        variant: 'success',
      });

      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      toast({
        title: 'Erro ao copiar',
        description: 'Não foi possível copiar o código PIX',
        variant: 'destructive',
      });
    }
  };

  const expirationDate = new Date(pixData.expirationDate);
  const timeUntilExpiration = expirationDate.getTime() - Date.now();
  const hoursUntilExpiration = Math.floor(timeUntilExpiration / (1000 * 60 * 60));

  return (
    <div className="space-y-6">
      {/* QR Code */}
      <div className="flex justify-center">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          {pixData.qrCode ? (
            <img
              src={`data:image/png;base64,${pixData.qrCode}`}
              alt="QR Code PIX"
              className="w-64 h-64"
            />
          ) : (
            <QRCode value={pixData.copyPaste} size={256} />
          )}
        </div>
      </div>

      {/* Instruções */}
      <div className="bg-brand-gold/10 border border-brand-gold/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <QrCodeIcon className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" />
          <div className="text-sm text-brand-ivory">
            <p className="font-medium mb-1 text-brand-gold">Como pagar com PIX:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Abra o app do seu banco</li>
              <li>Escolha pagar com PIX QR Code</li>
              <li>Escaneie o código acima</li>
              <li>Confirme o pagamento</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Código Copia e Cola */}
      <div>
        <label className="block text-sm font-medium text-brand-ivory/60 mb-2">
          Ou copie o código PIX:
        </label>
        <div className="relative">
          <textarea
            value={pixData.copyPaste}
            readOnly
            rows={3}
            className="w-full p-3 pr-24 bg-brand-smoke/50 border border-brand-gold/20 rounded-lg font-mono text-xs resize-none text-brand-ivory focus:ring-2 focus:ring-brand-gold focus:border-brand-gold"
          />
          <Button
            onClick={handleCopy}
            variant={copied ? 'premium' : 'outline'}
            size="sm"
            className="absolute right-2 top-2"
          >
            {copied ? (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Copiado!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-2" />
                Copiar
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Validade */}
      <div className="bg-brand-gold/10 border border-brand-gold/30 rounded-lg p-4">
        <p className="text-sm text-brand-ivory">
          <strong className="text-brand-gold">Atenção:</strong> Este código PIX expira em{' '}
          <strong className="text-brand-gold">{hoursUntilExpiration} horas</strong>.
          Após o pagamento, seu pedido será confirmado automaticamente.
        </p>
      </div>

      {/* Status */}
      <div className="bg-brand-smoke/50 border border-brand-gold/20 rounded-lg p-4 text-center">
        <div className="animate-pulse">
          <div className="inline-block w-3 h-3 bg-brand-gold rounded-full mr-2"></div>
          <span className="text-sm text-brand-ivory">
            Aguardando pagamento...
          </span>
        </div>
        <p className="text-xs text-brand-ivory/60 mt-2">
          A confirmação é automática e leva alguns segundos
        </p>
      </div>
    </div>
  );
}
